import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { PageWrapper, Paper, FlexTableRow, FlexTableCol, TargetStatus } from 'virtues-bootstrap';
import { map } from 'ramda';
import { requestPatientList } from 'services/api/request/patient';
import { requestAlerts } from 'services/api/request/observation';
import { handleAlertsData } from 'store/selectors/observation';
import { mergeAlertsInfoWithPatientName } from 'store/selectors/reports/recent';
import usePagination from 'hook/usePagination';
import useDependentData from 'hook/useDependentData';
import PaginationSection from 'components/ui/PaginationSection';
import PaperWithLoader from 'components/ui/PaperWithLoader';

import { PATIENT_ID, NAME } from './constant';
import HeaderSearch from './components/HeaderSearch';
import HeaderColumn from './components/HeaderColumn';
import s from './styles.module.scss';

function Alerts({ t, userId, isSuperResearcher, isSuperTechnician, isResearcher }) {
  const performer = isSuperResearcher || isSuperTechnician ? null : userId;

  const [{ data, pagination, sorting }, getData] = usePagination({
    request: requestAlerts,
    selector: handleAlertsData,
    params: { count: 12, sort: 'patient', performer },
  });
  const { total, page, count } = pagination;
  const { sort, desc } = sorting;
  const isAvailableName = !(isSuperResearcher || isSuperTechnician);
  const isAvailableReadOn = isSuperResearcher || isResearcher;

  const [{ data: alertsWithPatientName, initial, fetching, error }] = useDependentData({
    request: requestPatientList,
    data,
    canWork: data && data.length,
    params: {
      elements: 'name',
      generalPracitioner: performer,
    },
    modifiers: [mergeAlertsInfoWithPatientName],
  });

  const searchFields = [
    ...(isAvailableName ? [{ key: t('patient.name'), value: NAME }] : []),
    { key: t('patient.id'), value: PATIENT_ID },
  ];

  const handlePageChange = selectedPage => {
    getData({
      pagesOffset: count * selectedPage,
      page: selectedPage,
    });
  };

  const headerColumns = [
    ...(isAvailableName ? [{ title: t('alerts.name') }] : []),
    { title: t('alerts.id'), key: 'patient' },
    { title: t('alerts.deviceStatus'), key: 'status' },
    // { title: t('alerts.lastUpdated'), key: '_lastUpdated' },
    ...(isAvailableReadOn ? [{ title: t('alerts.reviewedOn') }] : []),
    ...(isAvailableReadOn ? [{ title: t('alerts.openedOn') }] : []),
    ...(isAvailableReadOn ? [{ title: t('alerts.markedReadOn') }] : []),
  ];

  return (
    <PageWrapper>
      <HeaderSearch onSubmit={getData} searchFields={searchFields} />
      <PaperWithLoader
        error={error}
        loading={initial || fetching}
        check={!!total}
        placeholder={t('alerts.alertsNotFound')}
      >
        <Paper className={s.mainPaper}>
          <FlexTableRow heading>
            {headerColumns.map(({ title, key }) => (
              <HeaderColumn
                key={title}
                sort={sort}
                currentKey={key}
                title={title}
                desc={desc}
                handleClick={getData}
              />
            ))}
          </FlexTableRow>
          {map(
            ({ id, name, status, patientId, reviewedOn, openedOn, markedReadOn }) => (
              <FlexTableRow
                key={id}
                className={s.row}
                to={`/patient/${patientId}/transmission-report/${id}`}
                component={Link}
              >
                {isAvailableName ? (
                  <FlexTableCol>
                    <strong>{name || `RPM-CIED-${patientId}`}</strong>
                  </FlexTableCol>
                ) : null}
                <FlexTableCol>{`RPM-CIED-${patientId}`}</FlexTableCol>
                <FlexTableCol flex>
                  <TargetStatus className={s.status} variant={status} data-test={status} />
                </FlexTableCol>
                <FlexTableCol>{reviewedOn}</FlexTableCol>
                {isAvailableReadOn ? <FlexTableCol>{openedOn}</FlexTableCol> : null}
                {isAvailableReadOn ? <FlexTableCol>{markedReadOn}</FlexTableCol> : null}
              </FlexTableRow>
            ),
            alertsWithPatientName,
          )}
        </Paper>
        <PaginationSection
          activePage={page}
          disableInitialCallback
          onPageChange={handlePageChange}
          itemsPerPage={count}
          textKey="pagination.alerts"
          total={total}
        />
      </PaperWithLoader>
    </PageWrapper>
  );
}

Alerts.propTypes = {
  userId: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  isSuperTechnician: PropTypes.bool.isRequired,
  isResearcher: PropTypes.bool.isRequired,
  isSuperResearcher: PropTypes.bool.isRequired,
};

export default Alerts;
